import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  GitHub as GitHubIcon
} from '@mui/icons-material';

const Profile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    picture: '',
    given_name: '',
    family_name: '',
    phone: '',
    bio: '',
    website: '',
    company: '',
    position: '',
    linkedin_url: '',
    twitter_url: '',
    github_url: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        picture: user.picture || '',
        given_name: user.given_name || '',
        family_name: user.family_name || '',
        phone: user.phone || '',
        bio: user.bio || '',
        website: user.website || '',
        company: user.company || '',
        position: user.position || '',
        linkedin_url: user.linkedin_url || '',
        twitter_url: user.twitter_url || '',
        github_url: user.github_url || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await updateUser('/profile', formData);
      
      if (result && result.success) {
        setSuccess('Profile updated successfully!');
        // If this is a new user, redirect to dashboard after profile completion
        if (location.state?.newUser) {
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const isGoogleUser = user.authProvider === 'google';

  return (
    <>
      <SEO 
        title="Profile | Gravity Point Media" 
        description="Manage your Gravity Point Media account profile and settings." 
      />
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Box textAlign="center" mb={4}>
            <Avatar
              src={formData.picture}
              sx={{
                width: 100,
                height: 100,
                margin: '0 auto',
                mb: 2,
                bgcolor: theme.palette.primary.main,
                fontSize: '2.5rem'
              }}
            >
              {!formData.picture && <PersonIcon sx={{ fontSize: '3rem' }} />}
            </Avatar>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}
            >
              Profile Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your account information and preferences
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="given_name"
                  value={formData.given_name}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="family_name"
                  value={formData.family_name}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isGoogleUser}
                  InputProps={{
                    startAdornment: (
                      <EmailIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
                {isGoogleUser && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Email cannot be changed for Google accounts
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <PhoneIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <BusinessIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <WorkIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  InputProps={{
                    startAdornment: (
                      <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://"
                  InputProps={{
                    startAdornment: (
                      <LinkIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/"
                  InputProps={{
                    startAdornment: (
                      <LinkedInIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Twitter URL"
                  name="twitter_url"
                  value={formData.twitter_url}
                  onChange={handleChange}
                  placeholder="https://twitter.com/"
                  InputProps={{
                    startAdornment: (
                      <TwitterIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="GitHub URL"
                  name="github_url"
                  value={formData.github_url}
                  onChange={handleChange}
                  placeholder="https://github.com/"
                  InputProps={{
                    startAdornment: (
                      <GitHubIcon color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default Profile;